/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanDeactivateFn, RouterStateSnapshot } from '@angular/router';
import { isRouterStateLazyActivator } from '@gh/core-lazy';
import { DialogService } from '@gh/core-ui';
import { wrapInObservable } from '@gh/core-util';
import { FALSE$, interval$, of$, throwError$, TRUE$ } from '@gh/rx/index';
import {
  map$,
  catchError$,
  concatMap$,
  filter$,
  first$,
  scan$,
  switchMap$,
} from '@gh/rx/operators';
import invariant from 'invariant';
import { isFunction, isNil, reverse } from 'lodash';
import { Observable } from 'rxjs';
import { CanLeaveRoute } from './can-leave.interfaces';

function closeAllDialogs(dialogs: DialogService): Observable<boolean> {
  const { openedDialogs } = dialogs;
  if (openedDialogs.length === 0) {
    return TRUE$;
  }
  return of$(...reverse(openedDialogs)).pipe(
    concatMap$((dialogRef) => {
      const { componentInstance } = dialogRef;
      if (isNil(componentInstance)) {
        dialogRef.close();
        return TRUE$;
      }

      const isDeactivatable = componentInstance['canDeactivate'];
      if (!isDeactivatable) {
        dialogRef.close();
        return TRUE$;
      }

      return wrapInObservable(componentInstance.canDeactivate()).pipe(
        switchMap$((allowToClose) => {
          if (allowToClose) {
            dialogRef.close();
            return TRUE$;
          } else {
            return throwError$('break');
          }
        }));
    }),
    scan$((sum: number, _: boolean) => sum + 1, 0),
    filter$((sum) => sum === openedDialogs.length),
    map$(() => true),
    catchError$(() => FALSE$));
}

export const leaveGuard: CanDeactivateFn<CanLeaveRoute> = (
  component: CanLeaveRoute,
  route: ActivatedRouteSnapshot,
  _: RouterStateSnapshot,
  nextState?: RouterStateSnapshot,
): Observable<boolean> | Promise<boolean> | boolean => {
  if (nextState && isRouterStateLazyActivator(nextState)) {
    return true;
  }

  const dialogs = inject(DialogService);

  return closeAllDialogs(dialogs).pipe(
    switchMap$((allowToClose) => {
      if (!allowToClose) {
        return FALSE$;
      }

      const supportsLeave = route.data['leave'] || (!!route.data['contextRoute'] && !!route.data['contextRoute'].canDeactivate);
      if (supportsLeave) {
        invariant(
          isFunction(component.canDeactivate),
          `Component for route ${route.toString()} does not have canDeactivate function`,
        );
        return interval$(1).pipe(
          first$(),
          switchMap$(() => wrapInObservable<boolean>(component.canDeactivate(nextState))));
      } else {
        return TRUE$;
      }
    }));
};
